import { cn } from "@tine/designsystem-utils";
import { type ReactNode, useEffect, useRef, useState } from "react";

import Calendar, { type CalendarProps } from "../calendar/Calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../popover/Popover";

type DatePickerProps = Omit<CalendarProps, "onDatesChange"> & {
   render: (dates: Date[], isPickerOpen: boolean, openPicker: (show: boolean) => void) => ReactNode;
   hide?: boolean;
   inline?: boolean;
   onHide?: () => void;
   onDatesChange?: (dates: Date[]) => void;
   closeOnChange?: boolean;
   children?: ReactNode;
};

const DatePicker = ({
   render,
   mode,
   dates,
   size = "compact",
   closeOnChange,
   hide,
   inline,
   onHide = () => {},
   children,
   ...props
}: DatePickerProps) => {
   const onDatesChange: (dates: Date[]) => void = props.onDatesChange || (() => {});

   const [selectedDates, setSelectedDates] = useState<Date[]>(dates);
   const [show, setShow] = useState<boolean>(false);
   const ref = useRef<HTMLDivElement>(null);

   useEffect(() => {
      if (hide && show) {
         setSelectedDates(dates);
         const referenceElement: HTMLElement | null = ref.current?.querySelector("input,button") || null;
         setShow(false);
         referenceElement?.focus();
         onHide();
      }
   }, [hide]);

   useEffect(() => {
      if (show) {
         setSelectedDates(dates);
      }
   }, [show]);

   const handleChange = (dates: Date[]) => {
      setSelectedDates(dates);
      onDatesChange(dates);
      if (closeOnChange) {
         if (mode !== "range" || dates.length === 2) {
            const referenceElement: HTMLElement | null = ref.current?.querySelector("input,button") || null;
            setShow(false);
            referenceElement?.focus();
         }
      }
   };

   const calendarProps: CalendarProps = {
      ...props,
      dates,
      mode,
      onDatesChange: handleChange,
      isArrowFocus: true
   };

   const calendarWrapperCN = cn({
      "m-3": size === "compact",
      "m-4": size === "regular"
   });

   const keysWrapperCN = cn(calendarWrapperCN, "flex flex-col justify-between gap-1 px-3 sm:flex-row");

   useEffect(() => {
      const referenceElement: HTMLElement | null = ref.current?.querySelector("input,button") || null;
      const handleBlur = (e: FocusEvent) => {
         if (e.target !== referenceElement && ref.current && !ref.current.contains(e.relatedTarget as Node)) {
            e.preventDefault();
            e.stopPropagation();
            referenceElement?.focus();
         }
      };
      if (ref.current) {
         ref.current.addEventListener("focusout", handleBlur);
      }
      return () => ref.current?.removeEventListener("focusout", handleBlur);
   }, []);

   if (inline && show) {
      return (
         <div>
            <Calendar {...calendarProps} />
            <div className={keysWrapperCN}>{children}</div>
         </div>
      );
   }

   return (
      <Popover placement="bottom" open={show} onOpenChange={setShow}>
         <PopoverTrigger asChild>
            <div>{render(selectedDates, show, setShow)}</div>
         </PopoverTrigger>
         <PopoverContent>
            <div className={calendarWrapperCN}>
               <Calendar {...calendarProps} />
            </div>
            <div className={keysWrapperCN}>{children}</div>
         </PopoverContent>
      </Popover>
   );
};

export default DatePicker;
